import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Save } from 'lucide-react';
import UsernameManagement from '../UsernameManagement';
import FieldError from '../ui/FieldError';
import EncryptedBadge from '../security/EncryptedBadge';
import SearchableSelect from '../SearchableSelect';
import COUNTRIES from '../../data/countries';
import { validateRequired, validateMinLength, validateEmail } from '../../utils/validation';

const COUNTRY_OPTIONS = COUNTRIES.map(c => ({ value: c, label: c, searchKey: c }));

const ProfileSection = ({ profileData, setProfileData, onSubmit, isLoading }) => {
  const [touched, setTouched] = useState({});
  const onBlur = (field) => setTouched(t => ({ ...t, [field]: true }));

  const errors = {
    name: touched.name ? (validateRequired(profileData.name, 'Full name') || validateMinLength(profileData.name, 2, 'Full name')) : '',
    email: touched.email ? validateEmail(profileData.email) : '',
    country: touched.country ? validateRequired(profileData.country, 'Country') : '',
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, country: true });
    const allErrors = {
      name: validateRequired(profileData.name, 'Full name') || validateMinLength(profileData.name, 2, 'Full name'),
      email: validateEmail(profileData.email),
      country: validateRequired(profileData.country, 'Country'),
    };
    if (Object.values(allErrors).some(e => e)) return;
    onSubmit(e);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="w-5 h-5" />
            <span>Profile Information</span>
          </CardTitle>
          <CardDescription>Update your personal information</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                <EncryptedBadge variant="inline" />
              </div>
              <Input id="name" type="text" value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                onBlur={() => onBlur('name')}
                placeholder="Enter your full name"
                className={errors.name ? 'border-red-400' : ''} />
              <FieldError error={errors.name} />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                <EncryptedBadge variant="inline" />
              </div>
              <Input id="email" type="email" value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                onBlur={() => onBlur('email')}
                placeholder="Enter your email"
                className={errors.email ? 'border-red-400' : ''} />
              <FieldError error={errors.email} />
              <p className="text-sm text-slate-500">Changing your email will require verification</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country <span className="text-red-500">*</span></Label>
              <div className={errors.country ? '[&_button]:border-red-400' : ''}>
                <SearchableSelect
                  value={profileData.country || ''}
                  onValueChange={(value) => {
                    setProfileData({ ...profileData, country: value });
                    onBlur('country');
                  }}
                  options={COUNTRY_OPTIONS}
                  placeholder="Select your country"
                  searchPlaceholder="Search country..."
                  emptyMessage="No country found."
                  dataTestId="profile-country-select"
                />
              </div>
              <FieldError error={errors.country} />
              <p className="text-sm text-slate-500">Used to display appropriate pricing</p>
            </div>
            <Button type="submit" disabled={isLoading} className="w-full sm:w-auto">
              <Save className="w-4 h-4 mr-2" />
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </CardContent>
      </Card>
      <div className="mt-6">
        <UsernameManagement />
      </div>
    </>
  );
};

export default ProfileSection;
